import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Loader2, Search, BookOpen, LifeBuoy } from "lucide-react";
import { API } from "@/assets";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function HelpCenterPage() {
  const [articles, setArticles] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    document.title = "Help Center | DGE: Cold-Blooded Records LLC™";
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      fetch(`${API}/help/articles${q ? `?q=${encodeURIComponent(q)}` : ""}`)
        .then((r) => r.json())
        .then((d) => setArticles(d.articles || []))
        .catch(() => setArticles([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const categories = useMemo(() => ["All", ...new Set((articles || []).map((a) => a.category))], [articles]);
  const shown = (articles || []).filter((a) => category === "All" || a.category === category);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="help-center-page">
      <Navbar />
      <div className="pt-[70px]">
        <header className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-10 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-teal-300">Help Center</p>
          <h1 className="mt-2 font-display text-3xl sm:text-5xl font-bold text-white" data-testid="help-title">Knowledge Base</h1>
          <p className="mt-3 text-sm text-ice/60 max-w-xl mx-auto">Guides for every Medusa Room feature, membership questions, and troubleshooting — searchable, always here.</p>
          <div className="mt-6 relative max-w-md mx-auto">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ice/35" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guides — 'audio', 'XP', 'vault'…"
              className="w-full bg-[#14141A] border border-[#2a2a34] rounded-full pl-11 pr-4 py-3.5 text-sm text-ice focus:outline-none focus:border-teal-400/70"
              data-testid="help-search" />
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-5 sm:px-8 pb-20">
          {articles === null ? (
            <div className="flex justify-center py-14"><Loader2 className="animate-spin text-teal-300" size={24} /></div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-6 justify-center" data-testid="help-categories">
                {categories.map((c) => (
                  <button key={c} onClick={() => setCategory(c)}
                    className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-4 py-2 border transition-all ${category === c ? "border-teal-400 text-teal-300 bg-teal-500/10" : "border-white/15 text-ice/50 hover:border-teal-400/50"}`}
                    data-testid={`help-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c}
                  </button>
                ))}
              </div>
              {shown.length === 0 ? (
                <div className="text-center py-14" data-testid="help-empty">
                  <LifeBuoy size={26} className="mx-auto text-ice/30 mb-3" />
                  <p className="text-sm text-ice/50">No guides match that. Try a different search — or <Link to="/quote" className="text-teal-300 hover:text-teal-200">ask the label directly</Link>.</p>
                </div>
              ) : (
                <div className="space-y-2.5" data-testid="help-list">
                  {shown.map((a) => (
                    <div key={a.slug} className="rounded-2xl border border-[#26262e] bg-[#0B0B10]/80 overflow-hidden" data-testid={`help-article-${a.slug}`}>
                      <button onClick={() => setOpen(open === a.slug ? null : a.slug)}
                        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
                        data-testid={`help-toggle-${a.slug}`}>
                        <BookOpen size={15} className="text-teal-300 shrink-0" />
                        <span className="flex-1 font-display text-white text-sm font-bold">{a.title}</span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-ice/35 hidden sm:block">{a.category}</span>
                        <ChevronDown size={15} className={`text-ice/40 transition-transform ${open === a.slug ? "rotate-180" : ""}`} />
                      </button>
                      {open === a.slug && (
                        <div className="px-5 pb-5 pt-1 border-t border-white/5" data-testid={`help-body-${a.slug}`}>
                          <p className="text-sm text-ice/60 leading-relaxed whitespace-pre-line">{a.body}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-10 text-center text-xs text-ice/40">
                Still stuck? <Link to="/quote" className="text-teal-300 hover:text-teal-200 transition-colors" data-testid="help-contact-link">Contact the label</Link> — a human answers.
              </p>
            </>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
